/**
 * GCA Profile Data Structure Transformer
 * Transforms frontend form data to match GCA_PROFILE table structure
 * Based on 8 GCA entity types
 */

export interface GCAProfilePayload {
  // Universal columns (required for all types)
  gca_type: string;
  entity_name?: string;
  registration_number?: string;
  registration_date?: string;
  gca_status?: string;
  description?: string;
  website?: string;
  is_profile_verified?: boolean;
  verification_status?: string;

  // Government Institutions (TYPE_1)
  govt_institution_type?: string;
  ministry_department?: string;
  division_name?: string;
  district_name?: string;
  govt_institution_pan?: string;
  govt_institution_gst?: string;
  govt_institution_email?: string;
  govt_institution_contact_number?: string;

  // PSU (TYPE_2)
  psu_name?: string;
  psu_sector?: string;
  parent_ministry?: string;
  psu_headquarters?: string;
  psu_cin_gstin?: string;
  psu_gst_number?: string;
  psu_parent_details?: string;
  psu_subsidiaries_details?: string;
  psu_email?: string;
  psu_contact_number?: string;

  // NGO (TYPE_3)
  ngo_name?: string;
  ngo_registration_type?: string;
  ngo_sector?: string;
  ngo_focus_area?: string;
  principal_beneficiary?: string;
  ngo_objectives?: string;
  ngo_registration_number?: string;
  ngo_darpan_id?: string;
  ngo_status_12a_80g?: string;
  ngo_operation_active_states?: any;
  ngo_nature_of_work?: string;
  ngo_turnover?: number;
  ngo_pan_number?: string;
  ngo_gst_number?: string;
  ngo_email?: string;
  ngo_contact_number?: string;

  // Private Company (TYPE_4)
  company_name?: string;
  company_type?: string;
  cin_gstin?: string;
  company_industry?: string;
  company_sector?: string;
  company_headquarters?: string;
  annual_turnover?: number;
  employees_count?: number;
  company_pan?: string;
  company_gst_number?: string;
  company_parent_details?: string;
  company_subsidiaries_details?: string;
  company_nature_of_business?: string;
  company_states_of_operation?: any;
  company_incorporation_date?: string;
  company_email?: string;
  company_contact_number?: string;

  // Public Limited Company (TYPE_5)
  public_company_name?: string;
  public_cin_bse_nse?: string;
  listed_exchange?: string;
  sector_industry?: string;
  market_cap?: number;
  public_turnover?: number;
  public_employees_count?: number;
  plc_registered_company_name?: string;
  plc_cin_number?: string;
  plc_pan_number?: string;
  plc_gst_number?: string;
  plc_sebi_listing_status?: string;
  plc_parent_company_details?: string;
  plc_subsidiaries_details?: string;
  plc_email?: string;
  plc_contact_number?: string;

  // Philanthropies/Foundations (TYPE_6)
  foundation_name?: string;
  founder_name?: string;
  foundation_date?: string;
  foundation_type?: string;
  foundation_focus_area?: string;
  foundation_registration_number?: string;
  annual_grant_amount?: number;
  foundation_legal_status?: string;
  foundation_pan_number?: string;
  foundation_funding_source?: string;
  foundation_csr_esg_details?: string;
  foundation_email?: string;
  foundation_contact_number?: string;

  // Individual (TYPE_7)
  individual_fname?: string;
  individual_mname?: string;
  individual_lname?: string;
  individual_gender?: string;
  individual_dob?: string;
  individual_pan?: string;
  individual_aadhaar?: string;
  individual_occupation?: string;
  individual_organization?: string;
  individual_designation?: string;
  individual_expertise?: string;
  individual_full_name?: string;
  individual_date_of_birth?: string;
  individual_aadhaar_number?: string;
  individual_pan_number?: string;
  individual_occupation_details?: string;
  qualification?: string;
  profession?: string;
  years_of_experience?: string;
  areas_of_expertise?: any;
  specialization?: string;

  // Registered Group (TYPE_8)
  group_name?: string;
  group_type?: string;
  group_registration_number?: string;
  group_registration_date?: string;
  group_members_count?: number;
  group_focus_area?: string;
  group_sector?: string;
  group_objectives?: string;
  group_type_other?: string;

  // Cross-cutting fields
  representative_full_name?: string;
  representative_designation?: string;
  representative_email?: string;
  representative_phone?: string;
  representative_employee_id?: string;

  // Verification & Status fields
  bank_details?: Record<string, any>;
  gca_registration_status?: string;
  rejection_reason?: string;
  verification_date?: string;
  additional_info?: Record<string, any>;
}

/**
 * Transform form data to GCA Profile structure
 * Only includes fields relevant to the selected GCA type
 */
/**
 * Map form GCA type values to backend expected TYPE values
 */
function mapFormGCATypeToTypeEnum(formGcaType: string): string {
  const typeMapping: Record<string, string> = {
    GOVERNMENT_INSTITUTIONS: "TYPE_1",
    PSU: "TYPE_2",
    NGO: "TYPE_3",
    PRIVATE_COMPANY: "TYPE_4",
    PUBLIC_LIMITED_COMPANY: "TYPE_5",
    PHILANTHROPY: "TYPE_6",
    INDIVIDUAL: "TYPE_7",
    REGISTERED_GROUP: "TYPE_8",
  };
  return typeMapping[formGcaType] || formGcaType;
}

/**
 * Helper: Parse array data from various formats (JSON, comma-separated string, or array)
 */
function parseArrayData(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    // Try JSON parse first
    try {
      return JSON.parse(data);
    } catch (e) {
      // If JSON parse fails, treat as comma-separated string
      return data
        .split(",")
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    }
  }
  return [];
}

/**
 * Get organization type ID based on GCA type
 */
function getOrganizationTypeId(formGcaType: string): string {
  // Map form GCA types to backend UUID references from organizationType table
  const typeIdMapping: Record<string, string> = {
    GOVERNMENT_INSTITUTIONS: "0d984b49-b70c-449c-b493-505ffa32c115", // Government Institutions
    PSU: "2349ac97-480d-446d-a01b-5fc02d6617cf", // Public Sector Units
    NGO: "3cf19c7f-7c90-4d81-b297-43bc8cfbfa9da", // Non-Government Organisations
    PRIVATE_COMPANY: "4dc93647-093e-4bc7-a390-3f935b100ff3", // Private Companies
    PUBLIC_LIMITED_COMPANY: "e2215882-38bd-4bc6-a24e-569a40e54a19", // Public Limited Companies
    PHILANTHROPY: "9dbed480-de7b-42d5-96fd-be91b09c2da5", // Philanthropies
    REGISTERED_GROUP: "cfba641c-9026-4cd3-b0b4-5cde09bf1def", // Registered Group of Individuals
  };
  return typeIdMapping[formGcaType] || null;
}

export const transformFormDataToGCAProfile = (
  formData: any
): GCAProfilePayload => {
  const formGcaType = formData.gcaType;
  const gcaType = mapFormGCATypeToTypeEnum(formGcaType);
  const payload: GCAProfilePayload = {
    gca_type: gcaType,
    entity_name: extractOrganizationName(formData),
    registration_number:
      formData.registrationNumber ||
      formData.ngoRegistrationNumber ||
      formData.groupRegistrationNumber ||
      formData.foundationRegistrationNumber,
    registration_date:
      formData.registrationDate ||
      formData.incorporationDate ||
      formData.groupRegistrationDate ||
      formData.foundationDate,
    gca_status: formData.gcaStatus || "Pending",
    description: formData.description,
    website: formData.website,
    is_profile_verified: false,
    verification_status: "PENDING",
  };

  switch (formGcaType) {
    case "GOVERNMENT_INSTITUTIONS":
      payload.govt_institution_type = formData.typeOfInstitution;
      payload.ministry_department = formData.ministry;
      payload.division_name = formData.divisionName;
      payload.district_name = formData.districtName;
      payload.govt_institution_pan = formData.organizationPAN;
      payload.govt_institution_gst = formData.organizationGST;
      payload.govt_institution_email = formData.institutionalEmail;
      payload.govt_institution_contact_number = formData.officialContactNumber;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "PSU":
      payload.psu_name = formData.nameOfPSU;
      payload.psu_sector = formData.ownershipType || formData.psuSector;
      payload.parent_ministry = formData.parentMinistry;
      payload.psu_headquarters = formData.psuHeadquarters;
      payload.psu_cin_gstin = formData.cinNumber;
      payload.psu_gst_number = formData.gstNumber;
      payload.psu_parent_details = formData.parentCompanyDetails;
      payload.psu_subsidiaries_details = Array.isArray(
        formData.subsidiariesDetails
      )
        ? formData.subsidiariesDetails.join(", ")
        : formData.subsidiariesDetails;
      payload.psu_email = formData.primaryEmail;
      payload.psu_contact_number = formData.primaryContactNumber;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "NGO":
      payload.ngo_name = formData.nameOfNGO;
      payload.ngo_registration_type = formData.registrationType;
      payload.ngo_sector = formData.ngoSector;
      payload.ngo_focus_area = formData.focusArea || formData.natureOfWork;
      payload.principal_beneficiary = formData.principalBeneficiary;
      payload.ngo_objectives = formData.ngoObjectives;
      payload.ngo_registration_number = formData.ngoRegistrationNumber;
      payload.ngo_darpan_id = formData.ngoDarpanID;
      payload.ngo_status_12a_80g = formData.status12A80G;
      payload.ngo_operation_active_states = parseArrayData(
        formData.operationActiveStates
      );
      payload.ngo_nature_of_work = formData.natureOfWork;
      payload.ngo_turnover = formData.turnover
        ? parseFloat(formData.turnover)
        : null;
      payload.ngo_pan_number = formData.ngoPANNumber;
      payload.ngo_gst_number = formData.ngoGSTNumber;
      payload.ngo_email = formData.ngoEmail;
      payload.ngo_contact_number = formData.ngoContactNumber;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "PRIVATE_COMPANY":
      payload.company_name = formData.companyName;
      payload.company_type = formData.companyType;
      payload.cin_gstin = formData.cinNumber;
      payload.company_industry = formData.companyIndustry;
      payload.company_sector = formData.companySector;
      payload.company_headquarters = formData.companyHeadquarters;
      payload.annual_turnover = formData.annualTurnover
        ? parseFloat(formData.annualTurnover)
        : null;
      payload.employees_count = formData.employeesCount
        ? parseInt(formData.employeesCount)
        : null;
      payload.company_pan = formData.companyPAN;
      payload.company_gst_number = formData.companyGSTNumber;
      payload.company_parent_details = formData.parentCompanyDetailsPC;
      payload.company_subsidiaries_details = Array.isArray(
        formData.subsidiariesDetailsPC
      )
        ? formData.subsidiariesDetailsPC.join(", ")
        : formData.subsidiariesDetailsPC;
      payload.company_nature_of_business = formData.natureOfBusinessPC;
      payload.company_states_of_operation = parseArrayData(
        formData.statesOfOperationPC
      );
      payload.company_incorporation_date = formData.incorporationDate;
      payload.company_email = formData.companyEmail;
      payload.company_contact_number = formData.companyMobileNumber;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "PUBLIC_LIMITED_COMPANY":
      payload.public_company_name =
        formData.registeredCompanyName || formData.publicCompanyName;
      payload.public_cin_bse_nse = formData.plcCINBseNse;
      payload.listed_exchange = formData.listedExchange;
      payload.sector_industry = formData.sectorIndustryPLC;
      payload.market_cap = formData.marketCap
        ? parseFloat(formData.marketCap)
        : null;
      payload.public_turnover = formData.publicTurnover
        ? parseFloat(formData.publicTurnover)
        : null;
      payload.public_employees_count = formData.publicEmployeesCount
        ? parseInt(formData.publicEmployeesCount)
        : null;
      payload.plc_registered_company_name = formData.registeredCompanyName;
      payload.plc_cin_number = formData.plcCINNumber;
      payload.plc_pan_number = formData.plcPANNumber;
      payload.plc_gst_number = formData.plcGSTNumber;
      payload.plc_sebi_listing_status = formData.sebiListingStatus;
      payload.plc_parent_company_details = formData.parentCompanyDetailsPLC;
      payload.plc_subsidiaries_details = Array.isArray(
        formData.subsidiariesDetailsPLC
      )
        ? formData.subsidiariesDetailsPLC.join(", ")
        : formData.subsidiariesDetailsPLC;
      payload.plc_email = formData.corporateOfficeEmail;
      payload.plc_contact_number = formData.contactNumberPLC;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "PHILANTHROPY":
      payload.foundation_name = formData.foundationName;
      payload.founder_name = formData.founderName;
      payload.foundation_date = formData.foundationDate;
      payload.foundation_type = formData.foundationType;
      payload.foundation_focus_area = formData.foundationFocusArea;
      payload.foundation_registration_number =
        formData.foundationRegistrationNumber;
      payload.annual_grant_amount = formData.annualGrantAmount
        ? parseFloat(formData.annualGrantAmount)
        : null;
      payload.foundation_legal_status = formData.legalStatusFoundation;
      payload.foundation_pan_number = formData.foundationPANNumber;
      payload.foundation_funding_source = formData.fundingSource;
      payload.foundation_csr_esg_details = formData.csrEsgDetails;
      payload.foundation_email = formData.foundationEmail;
      payload.foundation_contact_number = formData.foundationMobile?.number;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    case "INDIVIDUAL":
      payload.individual_fname = extractFirstName(formData.individualFullName);
      payload.individual_mname = formData.individualMiddleName;
      payload.individual_lname = extractLastName(formData.individualFullName);
      payload.individual_gender = formData.individualGender;
      payload.individual_dob = formData.individualDateOfBirth;
      payload.individual_pan = formData.individualPANNumber;
      payload.individual_aadhaar = formData.individualAadhaarNumber;
      payload.individual_occupation = formData.individualOccupation;
      payload.individual_organization = formData.individualOrganization;
      payload.individual_designation = formData.individualDesignation;
      payload.individual_expertise = formData.individualExpertise;
      payload.individual_full_name = formData.individualFullName;
      payload.individual_date_of_birth = formData.individualDateOfBirth;
      payload.individual_aadhaar_number = formData.individualAadhaarNumber;
      payload.individual_pan_number = formData.individualPANNumber;
      payload.individual_occupation_details =
        formData.individualOccupationDetails;
      payload.qualification = formData.qualification;
      payload.profession = formData.profession;
      payload.years_of_experience = formData.yearsOfExperience;
      payload.areas_of_expertise = parseArrayData(formData.areasOfExpertise);
      payload.specialization = formData.specialization;
      break;

    case "REGISTERED_GROUP":
      payload.group_name = formData.groupName;
      payload.group_type = formData.groupType;
      payload.group_registration_number = formData.groupRegistrationNumber;
      payload.group_registration_date = formData.groupRegistrationDate;
      payload.group_members_count = formData.groupMembersCount
        ? parseInt(formData.groupMembersCount)
        : null;
      payload.group_focus_area = formData.groupFocusArea;
      payload.group_sector = formData.groupSector;
      payload.group_objectives = formData.groupObjectives;
      payload.group_type_other = formData.groupTypeOther;
      payload.representative_employee_id = formData.repEmpId;
      payload.representative_full_name = formData.repFullName;
      payload.representative_designation = formData.repDesignation;
      payload.representative_email = formData.repEmail;
      payload.representative_phone = formData.repMobile?.number;
      break;

    default:
      console.warn(`Unknown GCA type: ${formGcaType}`);
  }

  // Add cross-cutting fields for all types
  payload.representative_full_name =
    payload.representative_full_name || formData.repFullName;
  payload.representative_designation =
    payload.representative_designation || formData.repDesignation;
  payload.representative_email =
    payload.representative_email || formData.repEmail;
  payload.representative_phone =
    payload.representative_phone || formData.repMobile?.number;
  payload.representative_employee_id =
    payload.representative_employee_id || formData.repEmpId;
  payload.representative_employee_id =
    payload.representative_employee_id || formData.repEmployeeID;

  // Add bank details if available
  if (formData.bankDetails) {
    payload.bank_details = formData.bankDetails;
  }

  // Add additional info if available
  if (formData.additionalInfo) {
    payload.additional_info = formData.additionalInfo;
  }

  // Remove undefined and null values to keep payload clean
  Object.keys(payload).forEach((key) => {
    const value = payload[key as keyof GCAProfilePayload];
    if (value === undefined || value === null || value === "") {
      delete payload[key as keyof GCAProfilePayload];
    }
  });

  return payload;
};

export const transformFormDataToRegistrationPayload = (formData: any) => {
  const formGcaType = formData.gcaType;
  const typeEnum = mapFormGCATypeToTypeEnum(formGcaType);

  // Construct base payload matching GCARegistrationDTO
  const payload: any = {
    // Required user account fields
    firstName: extractFirstName(
      formData.repFullName || formData.individualFullName
    ),
    email: extractUserEmail(formData),
    phoneNumber: extractUserPhoneNumber(formData),
    countryCode: formData.repMobile?.countryCode || "+91",
    designation:
      formGcaType === "INDIVIDUAL"
        ? formData.individualOccupation || formData.repDesignation || ""
        : formData.repDesignation || "",
    password: formData.password,
    roleId: "e296e7da-0dce-4f6b-90d7-ba44b92d47c1",

    // GCA Profile fields
    gca_type: typeEnum,
    entity_name: extractOrganizationName(formData), // Required

    // Type-specific fields (optional but can include)
    ...extractTypeSpecificFields(formData, formGcaType),

    // Comprehensive GCA Data with all type-specific fields
    gcaData: transformFormDataToGCAProfile(formData),

    // Organization data
    organization: buildOrganizationData(formData, formGcaType),

    // Addresses
    addresses: extractAddresses(formData),

    // Documents
    documents: extractDocuments(formData),
  };

  // Remove undefined values
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null) {
      delete payload[key];
    }
  });

  return payload;
};

/**
 * Helper: Extract type-specific fields based on GCA type
 */
function extractTypeSpecificFields(formData: any, formGcaType: string): any {
  const fields: any = {};

  switch (formGcaType) {
    case "GOVERNMENT_INSTITUTIONS":
      fields.govt_institution_type = formData.typeOfInstitution;
      fields.ministry_department = formData.ministry;
      break;

    case "PSU":
      fields.psu_name = formData.nameOfPSU;
      fields.psu_sector = formData.ownershipType;
      break;

    case "NGO":
      fields.ngo_name = formData.nameOfNGO;
      fields.ngo_registration_type = formData.registrationType;
      fields.ngo_registration_number = formData.ngoRegistrationNumber;
      fields.ngo_focus_area = formData.natureOfWork;
      break;

    case "PRIVATE_COMPANY":
      fields.company_name = formData.companyName;
      fields.cin_gstin = formData.companyPAN;
      break;

    case "PUBLIC_LIMITED_COMPANY":
      fields.public_company_name = formData.registeredCompanyName;
      break;

    case "PHILANTHROPY":
      fields.foundation_name = formData.foundationName;
      break;

    case "INDIVIDUAL":
      fields.individual_fname = extractFirstName(formData.individualFullName);
      fields.individual_lname = extractLastName(formData.individualFullName);
      fields.individual_gender = formData.individualGender;
      fields.individual_dob = formData.individualDateOfBirth;
      fields.individual_pan = formData.individualPANNumber;
      fields.individual_aadhaar = formData.individualAadhaarNumber;
      break;

    case "REGISTERED_GROUP":
      fields.group_name = formData.groupName;
      fields.group_type = formData.groupType;
      break;
  }

  return fields;
}

/**
 * Helper: Extract user email based on GCA type
 */
function extractUserEmail(formData: any): string {
  return (
    formData.repEmail ||
    formData.institutionalEmail ||
    formData.primaryEmail ||
    formData.ngoEmail ||
    formData.companyEmail ||
    formData.corporateOfficeEmail ||
    formData.foundationEmail ||
    formData.individualEmail ||
    formData.groupEmail ||
    ""
  );
}

/**
 * Helper: Extract user phone number based on GCA type
 */
function extractUserPhoneNumber(formData: any): string {
  return (
    formData.repMobile?.number ||
    formData.officialContactNumber ||
    formData.primaryContactNumber ||
    formData.ngoContactNumber ||
    formData.companyMobileNumber ||
    formData.contactNumberPLC ||
    formData.foundationMobile?.number ||
    formData.individualMobile?.number ||
    formData.groupContactNumber?.number ||
    ""
  );
}

/**
 * Helper: Build organization data from form data
 */
function buildOrganizationData(formData: any, formGcaType: string): any {
  // For INDIVIDUAL type, no organization is created
  if (formGcaType === "INDIVIDUAL") {
    return null;
  }

  const orgName = extractOrganizationName(formData);

  if (!orgName) {
    return null;
  }

  return {
    organizationName: orgName,
    organizationTypeId: getOrganizationTypeId(formGcaType),
    representativeDesignation: formData.repDesignation || "",
    documentDetail: buildDocumentDetails(formData, formGcaType),
  };
}

/**
 * Helper: Build document details array for organization
 * Includes organization-specific documents: CIN, PAN, GST, etc.
 */
function buildDocumentDetails(formData: any, formGcaType: string): any[] {
  const documents = [];
  const documentsByType: Record<string, any[]> = {};

  // If documents are already provided in formData.documents (from file uploads),
  // filter and include only organization-relevant documents
  if (formData.documents && Array.isArray(formData.documents)) {
    const organizationDocumentTypes = getOrganizationDocumentTypes(formGcaType);

    formData.documents.forEach((doc: any) => {
      // Include document if it's in the organization-relevant types
      if (organizationDocumentTypes.includes(doc.type)) {
        documents.push({
          serialNo: doc.serialNo,
          frontUrl: doc.frontUrl,
          backUrl: doc.backUrl || null,
          type: doc.type,
          isVerified: doc.isVerified || false,
          validFrom: doc.validFrom || null,
          validTill: doc.validTill || null,
        });
      }
    });

    // If we found org documents, return them
    if (documents.length > 0) {
      return documents;
    }
  }

  // Fallback: Extract documents based on form field mapping
  const organizationDocumentMapping =
    getOrganizationDocumentMapping(formGcaType);

  Object.entries(organizationDocumentMapping).forEach(
    ([formField, docType]) => {
      if (formData[formField]) {
        const fileList = Array.isArray(formData[formField])
          ? formData[formField]
          : [formData[formField]];

        if (!documentsByType[docType]) {
          documentsByType[docType] = [];
        }

        documentsByType[docType] = documentsByType[docType].concat(fileList);
      }
    }
  );

  // Convert to array format expected by backend with all required fields
  Object.entries(documentsByType).forEach(([type, files]) => {
    if (files && files.length > 0) {
      files.forEach((file) => {
        // Generate serialNo: use file.key if available, otherwise use type + timestamp
        const serialNo = file?.key || `${type}-${Date.now()}`;

        documents.push({
          serialNo: serialNo,
          frontUrl: file?.file || file?.key || "",
          backUrl: file?.backUrl || null,
          type: type,
          isVerified: file?.isVerified || false,
          validFrom: file?.validFrom || null,
          validTill: file?.validTill || null,
        });
      });
    }
  });

  return documents;
}

/**
 * Helper: Extract CIN number from form data
 */
function extractCINNumber(formData: any): string | null {
  return formData.cinNumber || formData.plcCINNumber || null;
}

/**
 * Helper: Extract organization name based on GCA type
 */
function extractOrganizationName(formData: any): string {
  const mapping: Record<string, string> = {
    GOVERNMENT_INSTITUTIONS: formData.nameOfInstitution || "",
    PSU: formData.nameOfPSU || "",
    NGO: formData.nameOfNGO || "",
    PRIVATE_COMPANY: formData.companyName || "",
    PUBLIC_LIMITED_COMPANY: formData.registeredCompanyName || "",
    PHILANTHROPY: formData.foundationName || "",
    INDIVIDUAL: formData.individualFullName || "",
    REGISTERED_GROUP: formData.groupName || "",
  };
  return mapping[formData.gcaType] || "";
}

/**
 * Helper: Extract first name from full name
 */
function extractFirstName(fullName?: string): string {
  if (!fullName) return "";
  return fullName.split(" ")[0];
}

/**
 * Helper: Extract last name from full name
 */
function extractLastName(fullName?: string): string {
  if (!fullName) return "";
  const parts = fullName.split(" ");
  return parts.length > 1 ? parts.slice(1).join(" ") : "";
}

/**
 * Helper: Generate username from form data
 */
function generateUserName(formData: any): string {
  const emailPrefix = (
    formData.repEmail ||
    formData.institutionalEmail ||
    ""
  ).split("@")[0];
  if (emailPrefix) return emailPrefix;

  const namePrefix = (formData.repFullName || "")
    .replace(/\s/g, "")
    .toLowerCase();
  return namePrefix || `gca_${Date.now()}`;
}

/**
 * Helper: Map GCA type to entity type
 */
function mapGCATypeToEntityType(gcaType: string): string {
  const mapping: Record<string, string> = {
    GOVT: "GOVERNMENT_INSTITUTION",
    PSU: "PSU",
    NGO: "NGO",
    PRIVATE_COMPANY: "PRIVATE_COMPANY",
    PUBLIC_LIMITED_COMPANY: "PUBLIC_LIMITED_COMPANY",
    PHILANTHROPY: "FOUNDATION",
    INDIVIDUAL: "INDIVIDUAL",
    REGISTERED_GROUP: "REGISTERED_GROUP",
  };
  return mapping[gcaType] || gcaType;
}

/**
 * Helper: Map GCA type to organization name
 */
function mapGCATypeToOrgName(formData: any): string {
  return extractOrganizationName(formData);
}

/**
 * Helper: Extract PAN number from form data
 */
function extractPANNumber(formData: any): string | null {
  return (
    formData.organizationPAN ||
    formData.ngoPANNumber ||
    formData.companyPAN ||
    formData.plcPANNumber ||
    formData.foundationPANNumber ||
    null
  );
}

/**
 * Helper: Extract GST number from form data
 */
function extractGSTNumber(formData: any): string | null {
  return (
    formData.organizationGST ||
    formData.ngoGSTNumber ||
    formData.companyGSTNumber ||
    formData.plcGSTNumber ||
    null
  );
}

/**
 * Helper: Extract addresses from form data
 * Creates different address entries for REGISTERED, OFFICE, and RESIDENTIAL as needed
 */
function extractAddresses(formData: any): any[] {
  const addresses = [];

  // Primary/Registered Address
  const primaryAddress = extractPrimaryAddress(formData);
  if (primaryAddress && primaryAddress.line1) {
    addresses.push({
      addressLine1: primaryAddress.line1,
      addressLine2: primaryAddress.line2 || "",
      city: primaryAddress.city || "Unspecified",
      district: primaryAddress.district || "Unspecified",
      state: primaryAddress.state || "Unspecified",
      zipCode: primaryAddress.zipCode || "00000", // Min 5 chars
      country: "India",
      isoCode: "IN",
      addressType: "REGISTERED",
    });
  }

  // Office/Secondary Address (for organizations)
  if (
    formData.gcaType !== "INDIVIDUAL" &&
    formData.gcaType !== "REGISTERED_GROUP"
  ) {
    const officeAddress = extractOfficeAddress(formData);
    if (officeAddress && officeAddress.line1) {
      addresses.push({
        addressLine1: officeAddress.line1,
        addressLine2: officeAddress.line2 || "",
        city: officeAddress.city || "Unspecified",
        district: officeAddress.district || "Unspecified",
        state: officeAddress.state || "Unspecified",
        zipCode: officeAddress.zipCode || "00000", // Min 5 chars
        country: "India",
        isoCode: "IN",
        addressType: "OFFICE",
      });
    }
  }

  // Residential Address (for individuals)
  if (formData.gcaType === "INDIVIDUAL") {
    const residentialAddress = extractIndividualResidentialAddress(formData);
    if (residentialAddress && residentialAddress.line1) {
      addresses.push({
        addressLine1: residentialAddress.line1,
        addressLine2: residentialAddress.line2 || "",
        city: residentialAddress.city || "Unspecified",
        district: residentialAddress.district || "Unspecified",
        state: residentialAddress.state || "Unspecified",
        zipCode: residentialAddress.zipCode || "00000", // Min 5 chars
        country: "India",
        isoCode: "IN",
        addressType: "RESIDENTIAL",
      });
    }
  }

  // Return at least one address with all required fields populated
  if (addresses.length === 0) {
    addresses.push({
      addressLine1: "Not Specified",
      city: "Unspecified",
      district: "Unspecified",
      state: "Unspecified",
      zipCode: "00000",
      country: "India",
      isoCode: "IN",
      addressType: "REGISTERED",
    });
  }

  return addresses;
}

/**
 * Helper: Extract primary/registered address based on GCA type
 */
function extractPrimaryAddress(formData: any): any {
  let line1 = "";
  let city = "";
  let district = "";
  let state = "";
  let zipCode = "";

  switch (formData.gcaType) {
    case "GOVERNMENT_INSTITUTIONS":
      line1 = formData.address || "";
      city = formData.city || "";
      district = formData.district || "";
      state = formData.state || "";
      zipCode = formData.pincode || "";
      break;

    case "PSU":
      line1 = formData.officeAddress || "";
      city = formData.officeCity || "";
      district = formData.officeDistrict || "";
      state = formData.officeState || "";
      zipCode = formData.officePincode || "";
      break;

    case "NGO":
      line1 = formData.ngoOfficeAddress || "";
      city = formData.ngoOfficeCity || "";
      district = formData.ngoOfficeDistrict || "";
      state = formData.ngoOfficeState || "";
      zipCode = formData.ngoOfficePincode || "";
      break;

    case "PRIVATE_COMPANY":
      line1 = formData.pcOfficeAddress || "";
      city = formData.pcOfficeCity || "";
      district = formData.pcOfficeDistrict || "";
      state = formData.pcOfficeState || "";
      zipCode = formData.pcOfficePincode || "";
      break;

    case "PUBLIC_LIMITED_COMPANY":
      line1 = formData.registeredOffice || formData.plcOfficeAddress || "";
      city = formData.plcOfficeCity || "";
      district = formData.plcOfficeDistrict || "";
      state = formData.plcOfficeState || "";
      zipCode = formData.plcOfficePincode || "";
      break;

    case "PHILANTHROPY":
      line1 = formData.foundationOfficeAddress || "";
      city = formData.foundationOfficeCity || "";
      district = formData.foundationOfficeDistrict || "";
      state = formData.foundationOfficeState || "";
      zipCode = formData.foundationOfficePincode || "";
      break;

    case "INDIVIDUAL":
      line1 = formData.individualAddress || "";
      city = formData.individualCity || "";
      district = formData.individualDistrict || "";
      state = formData.individualState || "";
      zipCode = formData.individualPincode || "";
      break;

    case "REGISTERED_GROUP":
      line1 = formData.groupOfficeAddress || "";
      city = formData.groupOfficeCity || "";
      district = formData.groupOfficeDistrict || "";
      state = formData.groupOfficeState || "";
      zipCode = formData.groupOfficePincode || "";
      break;

    default:
      return null;
  }

  return line1 ? { line1, city, district, state, zipCode } : null;
}

/**
 * Helper: Extract office address for organizations
 */
function extractOfficeAddress(formData: any): any {
  // For organizations that have a separate office address field
  let line1 = "";
  let city = "";
  let district = "";
  let state = "";
  let zipCode = "";

  // Check if there's a different office address (for organizations with multiple addresses)
  if (formData.corporateOffice) {
    line1 = formData.corporateOffice;
    city = formData.corporateOfficeCity || "";
    district = formData.corporateOfficeDistrict || "";
    state = formData.corporateOfficeState || "";
    zipCode = formData.corporateOfficePincode || "";
  }

  return line1 ? { line1, city, district, state, zipCode } : null;
}

/**
 * Helper: Extract residential address for individuals
 */
function extractIndividualResidentialAddress(formData: any): any {
  const line1 = formData.individualAddress || "";
  const city = formData.individualCity || "";
  const district = formData.individualDistrict || "";
  const state = formData.individualState || "";
  const zipCode = formData.individualPincode || "";

  return line1 ? { line1, city, district, state, zipCode } : null;
}

/**
 * Helper: Extract documents metadata (file uploads handled separately)
 * Groups documents by type with proper categorization
 */
function extractDocuments(formData: any): any[] {
  const documents = [];
  const documentsByType: Record<string, any[]> = {};

  // Document mapping: form field -> backend document type (CIN, AADHAAR, PAN, CERTIFICATE)
  const documentTypeMapping = getDocumentTypeMapping(formData.gcaType);

  Object.entries(documentTypeMapping).forEach(([formField, docType]) => {
    if (formData[formField]) {
      const fileList = Array.isArray(formData[formField])
        ? formData[formField]
        : [formData[formField]];

      if (!documentsByType[docType]) {
        documentsByType[docType] = [];
      }

      documentsByType[docType] = documentsByType[docType].concat(fileList);
    }
  });

  // Convert to array format expected by backend with all required fields
  Object.entries(documentsByType).forEach(([type, files]) => {
    if (files && files.length > 0) {
      files.forEach((file) => {
        // Generate serialNo: use file.key if available, otherwise use type + timestamp
        const serialNo = file?.key || `${type}-${Date.now()}`;

        documents.push({
          serialNo: serialNo,
          frontUrl: file?.file || file?.key || "",
          backUrl: file?.backUrl || null,
          type: type,
          isVerified: file?.isVerified || false,
          validFrom: file?.validFrom || null,
          validTill: file?.validTill || null,
        });
      });
    }
  });

  // Add at least one document if none provided
  if (documents.length === 0) {
    documents.push({
      serialNo: `CERTIFICATE-${Date.now()}`,
      frontUrl: "Registration certificate",
      type: "CERTIFICATE",
      isVerified: false,
      validFrom: null,
      validTill: null,
    });
  }

  return documents;
}

/**
 * Helper: Get organization-specific document types based on GCA type
 * Returns list of document types that should be included in organization.documentDetail
 */
function getOrganizationDocumentTypes(gcaType: string): string[] {
  const organizationDocumentTypes: Record<string, string[]> = {
    GOVERNMENT_INSTITUTIONS: ["CERTIFICATE"],
    PSU: [
      "CIN",
      "PAN",
      "GST_CERTIFICATE",
      "PARENT_COMPANY_DETAILS",
      "SUBSIDIARIES_DETAILS",
    ],
    NGO: [
      "CERTIFICATE",
      "GST_CERTIFICATE",
      "PAN",
      "TAX_EXEMPTION_CERTIFICATE",
      "DARPAN_ID",
      "AUDITED_FINANCIAL_STATEMENTS",
    ],
    PRIVATE_COMPANY: [
      "CIN",
      "PAN",
      "GST_CERTIFICATE",
      "AUDITED_FINANCIAL_STATEMENTS",
    ],
    PUBLIC_LIMITED_COMPANY: [
      "CIN",
      "PAN",
      "GST_CERTIFICATE",
      "AUDITED_FINANCIAL_STATEMENTS",
      "STOCK_EXCHANGE_LISTING",
    ],
    PHILANTHROPY: [
      "CERTIFICATE",
      "PAN",
      "AUDITED_FINANCIAL_STATEMENTS",
      "CSR_ESG_CERTIFICATE",
    ],
    REGISTERED_GROUP: ["CERTIFICATE"],
  };

  return organizationDocumentTypes[gcaType] || [];
}

/**
 * Helper: Get organization-specific document mapping based on GCA type
 * Maps form fields to valid backend document types for organization.documentDetail
 * Includes CIN, PAN, GST, and type-specific documents
 */
function getOrganizationDocumentMapping(
  gcaType: string
): Record<string, string> {
  const organizationDocumentMapping: Record<string, Record<string, string>> = {
    GOVERNMENT_INSTITUTIONS: {
      uploadCertificate: "CERTIFICATE",
      uploadAuthorizationLetter: "CERTIFICATE",
    },

    PSU: {
      uploadCertificateOfIncorporation: "CIN",
      uploadPAN: "PAN",
      uploadGST: "GST_CERTIFICATE",
      uploadParentCompanyDetails: "PARENT_COMPANY_DETAILS",
      uploadSubsidiariesDetails: "SUBSIDIARIES_DETAILS",
    },

    NGO: {
      uploadNGORegistrationCertificate: "CERTIFICATE",
      uploadNGOGST: "GST_CERTIFICATE",
      uploadNGOPAN: "PAN",
      upload12A80GCertificates: "TAX_EXEMPTION_CERTIFICATE",
      uploadNGODarpanCertificate: "DARPAN_ID",
      uploadAuditedFinancialStatements: "AUDITED_FINANCIAL_STATEMENTS",
    },

    PRIVATE_COMPANY: {
      uploadCertificateOfIncorporationPC: "CIN",
      uploadPANPC: "PAN",
      uploadGSTCertificatePC: "GST_CERTIFICATE",
      uploadAuditedFinancialStatementsPC: "AUDITED_FINANCIAL_STATEMENTS",
    },

    PUBLIC_LIMITED_COMPANY: {
      uploadIncorporationDocumentsPLC: "CIN",
      uploadPANPLC: "PAN",
      uploadGSTPLC: "GST_CERTIFICATE",
      uploadAuditedFinancialStatementsPLC: "AUDITED_FINANCIAL_STATEMENTS",
      uploadStockExchangeListing: "STOCK_EXCHANGE_LISTING",
    },

    PHILANTHROPY: {
      uploadRegistrationCertificateFoundation: "CERTIFICATE",
      uploadPANFoundation: "PAN",
      uploadAuditedFinancialStatementsFoundation:
        "AUDITED_FINANCIAL_STATEMENTS",
      uploadCSREsgDocument: "CSR_ESG_CERTIFICATE",
    },

    REGISTERED_GROUP: {
      uploadGroupRegistrationCertificate: "CERTIFICATE",
    },
  };

  return organizationDocumentMapping[gcaType] || {};
}

/**
 * Helper: Get document type mapping based on GCA type
 * Maps form fields to valid backend document types: CIN, AADHAAR, PAN, CERTIFICATE
 */
export function getDocumentTypeMapping(
  gcaType: string
): Record<string, string> {
  const commonMapping: Record<string, string> = {
    uploadPAN: "PAN",
    uploadGST: "GST_CERTIFICATE",
    uploadRepIDProof: "CERTIFICATE",
    profilePhoto: "CERTIFICATE",
  };

  const typeSpecificMapping: Record<string, Record<string, string>> = {
    GOVERNMENT_INSTITUTIONS: {
      ...commonMapping,
      uploadCertificate: "CERTIFICATE",
      uploadAuthorizationLetter: "CERTIFICATE",
    },

    PSU: {
      ...commonMapping,
      uploadCertificateOfIncorporation: "CIN",
    },

    NGO: {
      ...commonMapping,
      uploadNGORegistrationCertificate: "CERTIFICATE",
      uploadNGOGST: "GST_CERTIFICATE",
      uploadNGOPAN: "PAN",
      upload12A80GCertificates: "TAX_EXEMPTION_CERTIFICATE",
      uploadNGODarpanCertificate: "DARPAN_ID",
      uploadAuditedFinancialStatements: "AUDITED_FINANCIAL_STATEMENTS",
    },

    PRIVATE_COMPANY: {
      ...commonMapping,
      uploadCertificateOfIncorporationPC: "CIN",
      uploadPANPC: "PAN",
      uploadGSTCertificatePC: "GST_CERTIFICATE",
      uploadAuditedFinancialStatementsPC: "AUDITED_FINANCIAL_STATEMENTS",
    },

    PUBLIC_LIMITED_COMPANY: {
      ...commonMapping,
      uploadIncorporationDocumentsPLC: "CIN",
      uploadPANPLC: "PAN",
      uploadGSTPLC: "GST_CERTIFICATE",
      uploadAuditedFinancialStatementsPLC: "AUDITED_FINANCIAL_STATEMENTS",
      uploadStockExchangeListing: "STOCK_EXCHANGE_LISTING",
    },

    PHILANTHROPY: {
      ...commonMapping,
      uploadRegistrationCertificateFoundation: "CERTIFICATE",
      uploadPANFoundation: "PAN",
      uploadAuditedFinancialStatementsFoundation:
        "AUDITED_FINANCIAL_STATEMENTS",
      uploadCSREsgDocument: "CSR_ESG_CERTIFICATE",
    },

    INDIVIDUAL: {
      ...commonMapping,
      uploadPassportPhoto: "CERTIFICATE",
      uploadIndividualAadhaar: "AADHAAR",
      uploadIndividualPAN: "PAN",
      qualificationCertificate: "QUALIFICATION_CERTIFICATE",
      experienceCertificate: "EXPERIENCE_CERTIFICATE",
      aadhaarDocument: "AADHAAR",
      panDocument: "PAN",
    },

    REGISTERED_GROUP: {
      ...commonMapping,
      uploadGroupRegistrationCertificate: "CERTIFICATE",
    },
  };

  return typeSpecificMapping[gcaType] || commonMapping;
}
